import * as React from 'react';

import { gql } from '@soundxyz/gql-string';
import { LandingRankedListItem } from '@components/Landing/shared/LandingRankedList/LandingRankedListItem';
import { FollowButton } from '@components/basics/FollowButton';
import { Avatar } from '@components/basics/Images/Avatar';
import { useFollowingStatus } from '@components/modals/UserStats/store';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, TopCollectorsRankedListItemFragmentDoc } from '@graphql/reactQuery';
import { formatWeiToEthForDisplay, getSingularOrPlural } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment TopCollectorsRankedListItem on TrendingCollectorInfo {
    nftsBought
    creatorsSupported
    userAddress
    totalSpent
    user {
      id
      publicAddress
      webappUri
      username
      avatar {
        id
        url
      }
      artist {
        id
      }
    }
  }
`);

interface Props {
  topCollectorFrag: FragmentType<TopCollectorsRankedListItemFragmentDoc>;
  rank: number;
}

/**
 * This is it's own component because trendingCollector
 * might not have a user, so we need to render this conditionally
 */
export function PrefetchUserFollowingStatus({ userId }: { userId: string }) {
  /**
   * Prefetch the following status for these users
   * so we don't see a button flash when hovering
   */
  useFollowingStatus({
    userId,
  });
  return null;
}

export const TopCollectorsRankedListItem = ({ topCollectorFrag, rank }: Props) => {
  const trendingCollector = getFragment(TopCollectorsRankedListItemFragmentDoc, topCollectorFrag);
  const { user } = trendingCollector;

  const userHref = React.useMemo(() => {
    if (user) {
      return webappUriPath(user.webappUri);
    } else {
      return `/user/${trendingCollector.userAddress}`;
    }
  }, [user, trendingCollector.userAddress]);

  const subtitleText = `${formatWeiToEthForDisplay({
    weiValue: trendingCollector.totalSpent,
    maxDecimals: 4,
  })} ETH`;

  const secondarySubtitleText = `${trendingCollector.nftsBought} ${getSingularOrPlural({
    word: 'mint',
    count: trendingCollector.nftsBought,
  })}`;

  return (
    <>
      {user && <PrefetchUserFollowingStatus userId={user.id} />}
      <LandingRankedListItem
        title={{
          text: trendingCollector.user?.username || trendingCollector.userAddress,
        }}
        cellHref={userHref}
        imageRadius="rounded-full"
        id={trendingCollector.userAddress}
        image={
          <Avatar
            src={user?.avatar?.url}
            username={user?.username}
            webappUri={userHref}
            walletAddress={user?.publicAddress || trendingCollector.userAddress}
            size={56}
            disableLink
          />
        }
        subtitle={{
          text: subtitleText,
        }}
        actionButton={
          trendingCollector.user ? (
            <FollowButton
              userId={trendingCollector.user.id}
              variant="notification"
              size="XS"
              sourceLocation="Top Collectors Hover Landing Page"
              artistId={trendingCollector.user.artist?.id}
            />
          ) : undefined
        }
        secondarySubtitle={{
          text: secondarySubtitleText,
        }}
        verified={Boolean(trendingCollector.user?.artist)}
        rank={rank}
      />
    </>
  );
};
